import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    Box,
    Divider,
    Grid,
    List,
    ListItem,
    Paper,
    Typography,
    useTheme,
    Skeleton,
    ListItemButton,
    styled
} from '@mui/material';
import random from 'lodash/random';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { ApplicationState } from 'store/store';
import { StyledMenuTitle } from './MenuSidebar';

const PREFIX = 'MenuDesktopSkeleton';

const classes = {
    categoryItem: `${PREFIX}categoryItem`,
    categoryItemText: `${PREFIX}categoryItemText`,
    scrollingElement: `${PREFIX}scrollingElement`
};

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(2),
    [`& .${classes.categoryItemText}`]: {
        fontWeight: 'inherit'
    }
}));

const StyledGridContainer = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(-2),
    height: `calc(100% + ${theme.spacing(2)})`,
    [`& .${classes.scrollingElement}`]: {
        height: '100%',
        paddingTop: '0 !important',
        paddingBottom: '0 !important'
    }
}));

export const MenuDesktopSkeleton: React.FC<React.PropsWithChildren> = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);
    const renderSidebarSkeleton = React.useCallback(
        (_: string, index: number) => (
            <StyledListItemButton key={index}>
                <Typography variant="body1" className={classes.categoryItemText}>
                    <Skeleton variant="text" width={random(30, 50)} />
                </Typography>
            </StyledListItemButton>
        ),
        []
    );

    const renderCategorySkeleton = React.useCallback(
        (_: string, index: number) => (
            <React.Fragment key={index}>
                <Box marginY={1} display="flex" justifyContent="space-between">
                    <Box>
                        <Skeleton width={random(40, 80)} variant="text" />
                        <Skeleton width={random(70, 150)} variant="text" />
                        <Skeleton width={40} variant="text" />
                    </Box>
                    {random(1, 0) === 1 && (
                        <Skeleton
                            variant="rectangular"
                            width={theme.spacing(10)}
                            height={theme.spacing(10)}
                        />
                    )}
                </Box>
                <Divider />
            </React.Fragment>
        ),
        [theme]
    );

    const renderMenuSkeleton = React.useCallback(
        (val: any, i: any) => (
            <div key={i + val}>
                <Skeleton height={230} width="100%" variant="rectangular" />
                <Box marginY={2}>
                    <Skeleton width={random(30, 70)} />
                </Box>
                <Divider />
                {Array(8).fill('').map(renderCategorySkeleton)}
            </div>
        ),
        [renderCategorySkeleton]
    );

    return (
        <>
            <MuiAppBar title={currentLocation ? currentLocation.title : 'Mechu'} />
            <Box paddingTop={4} height="100%" flex="1">
                <StyledGridContainer container spacing={2}>
                    <Grid size={4} className={classes.scrollingElement}>
                        <Box>
                            <Paper>
                                <Box paddingTop={3} paddingBottom={2}>
                                    <StyledMenuTitle variant="h6">{t('OUR_MENU')}</StyledMenuTitle>
                                </Box>
                                <List>{Array(8).fill('').map(renderSidebarSkeleton)}</List>
                            </Paper>
                        </Box>
                    </Grid>
                    <Grid size={8} className={classes.scrollingElement}>
                        <Paper>
                            <List>
                                <ListItem>
                                    <Box display="flex" flexDirection="column" width="100%">
                                        {Array(2).fill('').map(renderMenuSkeleton)}
                                    </Box>
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </StyledGridContainer>
            </Box>
        </>
    );
};
